import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useLayoutEffect, useRef } from 'react';
import { styled, useTheme } from 'styled-components';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import { useResizeObserver } from '@/js/libs/hooks/useResizeObserver/index.ts';
import { Media } from '../../../theme/Media.js';
import Typography from '../../Typography/index.js';
import { getColorStyles } from './Base.js';
import { CloseButton, InfoIcon, getColorByType } from './common.js';
const Bar = styled.div `
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 100;
`;
const Row = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  ${getColorStyles}
  border-width: 0 0 1px;
`;
const Content = styled.div `
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;
const Title = styled(Typography.TextMedium) `
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  ${({ theme }) => theme.typography.weightMedium}

  /* getColorStyles tints every TextMedium in the row with the type color; the title stays neutral. */
  && {
    color: ${({ theme }) => theme.colors.grey100_1};
  }
`;
const Meta = styled(Typography.TextSmall) `
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;

  &::before {
    content: '';
    width: 1px;
    height: 12px;
    background-color: ${({ theme }) => theme.colors.grey30_1};
  }

  @media ${Media.xsMax} {
    display: none;
  }
`;
const ActionButton = styled.button `
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  white-space: nowrap;
  color: ${({ $color }) => $color};
  ${({ theme }) => theme.typography.textSmall}
  ${({ theme }) => theme.typography.weightMedium}
`;
const AppBarNotificationRow = memo(function AppBarNotificationRow({ type = 'warning', item, actionLabel, onAction, onClose, }) {
    const theme = useTheme();
    const handleAction = useCallbackRef(() => onAction(item.id));
    const handleClose = useCallbackRef(() => onClose(item.id));
    return (_jsxs(Row, { $type: type, role: 'alert', children: [_jsxs(Content, { children: [_jsx(InfoIcon, { type: type }), _jsx(Title, { children: item.title }), item.meta && _jsx(Meta, { color: theme.colors.grey70_1, children: item.meta }), _jsx(ActionButton, { type: 'button', $color: getColorByType(theme, type), onClick: handleAction, children: actionLabel })] }), _jsx(CloseButton, { closeToast: handleClose })] }));
});
export const AppBarNotifications = memo(function AppBarNotifications({ items, onHeightChange, ...rowProps }) {
    const barRef = useRef(null);
    const reportHeight = useCallbackRef((height) => onHeightChange?.(height));
    const reportResize = useCallbackRef((rect) => reportHeight(rect.height));
    // The observer's first callback updates state outside a React event handler, so it is flushed after
    // the browser has painted — one frame with no offset, which is the overlap this bar has to avoid.
    // A layout effect runs before that paint.
    useLayoutEffect(() => {
        if (barRef.current)
            reportHeight(barRef.current.getBoundingClientRect().height);
    }, [reportHeight]);
    // Keeps the offset in step when rows are added or removed.
    useResizeObserver(barRef, reportResize);
    return (_jsx(Bar, { ref: barRef, children: items.map((item) => (_jsx(AppBarNotificationRow, { item: item, ...rowProps }, item.id))) }));
});
